import { MAX_SCHEDULE_MILESTONES } from "@common/constants/domains/accounting/schedules";
import { Button } from "@design-system/Button/Button";
import { PlusIcon } from "@icons";
import {
    addScheduleMilestone,
    isMilestoneAllocationLocked,
    removeScheduleMilestone,
    ScheduleMilestoneForm,
    setScheduleMilestoneAllocation,
} from "./scheduleMilestoneFormHelpers";
import type { MilestoneRowErrors } from "./scheduleMilestoneFormErrors";
import { ScheduleMilestoneRow } from "./ScheduleMilestoneRow";
import styles from "./ScheduleDrawer.module.scss";

export interface ScheduleMilestonePlanRowsProps {
    milestones: ScheduleMilestoneForm[];
    onChange: (milestones: ScheduleMilestoneForm[]) => void;
    /** Field messages per row, in row order; undefined where the row is valid. */
    errors?: ReadonlyArray<MilestoneRowErrors | undefined>;
    isReadOnly?: boolean;
}

/** The milestone plan as sentence rows plus Add milestone, with no form library. */
export const ScheduleMilestonePlanRows = ({
    milestones,
    onChange,
    errors,
    isReadOnly = false,
}: ScheduleMilestonePlanRowsProps) => {
    const updateMilestone = (
        index: number,
        patch: Partial<ScheduleMilestoneForm>,
    ) => {
        onChange(
            milestones.map((milestone, milestoneIndex) =>
                milestoneIndex === index
                    ? { ...milestone, ...patch }
                    : milestone,
            ),
        );
    };
    const canAdd = !isReadOnly && milestones.length < MAX_SCHEDULE_MILESTONES;

    return (
        <div className={styles.milestoneRows}>
            {milestones.map((milestone, index) => (
                <ScheduleMilestoneRow
                    key={milestone.key}
                    index={index}
                    milestone={milestone}
                    isAllocationLocked={isMilestoneAllocationLocked({
                        index,
                        count: milestones.length,
                    })}
                    isReadOnly={isReadOnly}
                    errors={errors?.[index]}
                    onChange={(patch) => updateMilestone(index, patch)}
                    onAllocationChange={(allocationPercent) =>
                        onChange(
                            setScheduleMilestoneAllocation({
                                milestones,
                                index,
                                allocationPercent,
                            }),
                        )
                    }
                    onRemove={
                        milestones.length > 1
                            ? () =>
                                  onChange(
                                      removeScheduleMilestone(
                                          milestones,
                                          index,
                                      ),
                                  )
                            : undefined
                    }
                />
            ))}
            {canAdd && (
                <div className={styles.milestoneAddRow}>
                    <Button
                        size="md"
                        type="button"
                        variant="secondary"
                        leftIcon={<PlusIcon />}
                        onClick={() =>
                            onChange(addScheduleMilestone(milestones))
                        }
                    >
                        Add milestone
                    </Button>
                </div>
            )}
        </div>
    );
};
